import React, { useState, useEffect } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Mail, MessageCircle, Copy } from "lucide-react";

const ago = (iso) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
};

export const LastSentNote = ({ last, testId, noun = "Form" }) => last ? (
  <span className="text-[11px] text-stone-500" data-testid={testId}>{noun} sent by {last.channel === "email" ? "email" : "WhatsApp"} · {ago(last.at)}{last.by ? ` · ${last.by}` : ""}</span>
) : null;

export const SendIntakeForm = ({ patientId, token, email, phone, lastSent, onSent, showCopy = false, compact = false, endpoint = "/staff/intake/send-form", noun = "Form", testPrefix = "send-intake" }) => {
  const [busy, setBusy] = useState(null);
  const [last, setLast] = useState(lastSent || null);
  useEffect(() => { setLast(lastSent || null); }, [lastSent]);
  const digits = (phone || "").replace(/[^0-9]/g, "");

  const send = async (channel) => {
    if (channel !== "copy" && last && Date.now() - new Date(last.at).getTime() < 3600000 && !window.confirm(`The ${noun.toLowerCase()} was already sent ${ago(last.at)}. Send again?`)) return;
    const win = channel === "whatsapp" ? window.open("", "_blank") : null;
    setBusy(channel);
    try {
      const r = await fetch(`${API}${endpoint}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ patient_id: patientId, channel, origin: window.location.origin, phone: phone || "" }),
      });
      const d = await r.json();
      if (!r.ok) { win?.close(); toast.error(d.detail || "Could not send form"); return; }
      if (channel === "whatsapp") {
        const url = `https://wa.me/${d.phone}?text=${encodeURIComponent(d.message)}`;
        win ? (win.location.href = url) : window.open(url, "_blank");
        toast.success(`WhatsApp opened with the ${noun.toLowerCase()} link. Press send in WhatsApp.`);
      } else if (channel === "email") toast.success(`${noun} emailed to the patient.`);
      if (channel === "copy") { await navigator.clipboard?.writeText(d.link); toast.success("Form link copied"); }
      setLast(d.last_sent); onSent?.(d.last_sent);
    } catch { win?.close(); toast.error("Connection error"); } finally { setBusy(null); }
  };

  const size = compact ? "h-7 text-[11px] px-2" : "text-xs";
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`${testPrefix}-${patientId}`}>
      <Button variant="outline" size="sm" className={`${size} rounded-lg`} disabled={!!busy || !email} title={!email ? "No email saved for this patient" : ""} onClick={() => send("email")} data-testid={`${testPrefix}-email-${patientId}`}>
        {busy === "email" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}Send via Email
      </Button>
      <Button variant="outline" size="sm" className={`${size} rounded-lg text-green-700 border-green-200 hover:bg-green-50`} disabled={!!busy || !digits} title={!digits ? "No phone number saved for this patient" : ""} onClick={() => send("whatsapp")} data-testid={`${testPrefix}-whatsapp-${patientId}`}>
        {busy === "whatsapp" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <MessageCircle className="w-3 h-3 mr-1" />}Send via WhatsApp
      </Button>
      {showCopy && <Button variant="ghost" size="sm" className={`${size} rounded-lg`} disabled={!!busy} onClick={() => send("copy")} data-testid={`copy-intake-link-${patientId}`}><Copy className="w-3 h-3 mr-1" />Copy link</Button>}
      {(!email || !digits) && <span className="text-[11px] text-stone-400">{!email && !digits ? "No email or phone saved" : !email ? "No email saved" : "No phone saved"}</span>}
      <LastSentNote last={last} noun={noun} testId={`${testPrefix}-last-sent-${patientId}`} />
    </div>
  );
};
